import React from 'react';
import { Navigate, Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { companyInfo } from '../data/mock';
import {
  LayoutDashboard,
  Briefcase,
  FileText,
  MessageSquare,
  Phone,
  LogOut,
  Users,
  History,
  Bell,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  Calculator,
  Shield,
  FolderOpen,
  Star,
  BarChart3,
  ClipboardList,
  Wallet,
  Car,
  Coins,
  Percent,
  Home,
  Map as MapIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useState, useEffect } from 'react';
import { useNotifications } from '../hooks/useNotifications';
import { NotificationBell } from '../components/NotificationBell';

const PortalLayout = () => {
  const { t } = useLanguage();
  const { user, logout, isAdmin, isAuthenticated, loading, isEmployee } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const notifications = useNotifications(isAuthenticated && !loading, location.pathname);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8faf8]" data-testid="portal-loading">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#1F3D2B] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#2E2E2E]/60">{t('portalUi.common.loading', 'Ielādē...')}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/portal/login" replace />;
  }

  const clientNavItems = [
    { path: '/portal/dashboard', label: t('portalUi.nav.client.dashboard', 'Pārskats'), icon: LayoutDashboard },
    { path: '/portal/projects', label: t('portalUi.nav.client.projects', 'Projekti'), icon: FolderOpen },
    { path: '/portal/jobs', label: t('portalUi.nav.client.jobs', 'Darbi'), icon: Briefcase },
    { path: '/portal/documents', label: t('portalUi.nav.client.documents', 'Dokumenti'), icon: FileText },
    { path: '/portal/messages', label: t('portalUi.nav.client.messages', 'Ziņojumi'), icon: MessageSquare },
    { path: '/portal/contact', label: t('portalUi.nav.client.contact', 'Kontakti'), icon: Phone },
  ];

  const adminNavItems = [
    { path: '/portal/admin', label: t('portalUi.nav.admin.dashboard', 'Pārskats'), icon: LayoutDashboard },
    { path: '/portal/admin/clients', label: t('portalUi.nav.admin.clients', 'Klienti'), icon: Users },
    { path: '/portal/admin/employees', label: t('portalUi.nav.admin.employees', 'Darbinieki'), icon: Shield },
    { path: '/portal/admin/employee-tasks', label: t('portalUi.nav.admin.employeeTasks', 'Darba uzdevumi'), icon: Briefcase },
    { path: '/portal/admin/work-entries', label: t('portalUi.nav.admin.workEntries', 'Darba atskaites'), icon: ClipboardList },
    { path: '/portal/admin/salary', label: t('portalUi.nav.admin.salary', 'Mēneša alga'), icon: Wallet },
    { path: '/portal/admin/work-types', label: t('portalUi.nav.admin.workTypes', 'Darba veidi un likmes'), icon: Calculator },
    { path: '/portal/admin/tax-settings', label: t('portalUi.nav.admin.taxSettings', 'Nodokļu likmes'), icon: Percent },
    { path: '/portal/admin/mileage', label: t('portalUi.nav.admin.mileage', 'Kilometri'), icon: Car },
    { path: '/portal/admin/profit', label: t('portalUi.nav.admin.profit', 'Objektu peļņa'), icon: Coins },
    { path: '/portal/admin/projects', label: t('portalUi.nav.admin.projects', 'Projekti'), icon: FolderOpen },
    { path: '/portal/admin/map', label: t('portalUi.nav.admin.map', 'Karte'), icon: MapIcon },
    { path: '/portal/admin/jobs', label: t('portalUi.nav.admin.jobs', 'Darbi'), icon: Briefcase },
    { path: '/portal/admin/documents', label: t('portalUi.nav.admin.documents', 'Dokumenti'), icon: FileText },
    { path: '/portal/admin/finance', label: t('portalUi.nav.admin.finance', 'Finanses'), icon: BarChart3 },
    { path: '/portal/admin/messages', label: t('portalUi.nav.admin.messages', 'Paziņojumi'), icon: Bell },
    { path: '/portal/admin/visitors', label: t('portalUi.nav.admin.visitors', 'Apmeklējumi'), icon: BarChart3 },
    { path: '/portal/admin/testimonials', label: t('portalUi.nav.admin.testimonials', 'Atsauksmes'), icon: Star },
    { path: '/portal/admin/audit', label: t('portalUi.nav.admin.audit', 'Audit logs'), icon: History },
  ];

  const employeeNavItems = [
    { path: '/portal/employee', label: t('portalUi.nav.employee.home', 'Sākums'), icon: Home },
    { path: '/portal/employee/tasks', label: t('portalUi.nav.employee.tasks', 'Mani darba uzdevumi'), icon: Briefcase },
    { path: '/portal/employee/entries', label: t('portalUi.nav.employee.entries', 'Manas atskaites'), icon: ClipboardList }
  ];

  const navItems = isAdmin ? adminNavItems : isEmployee ? employeeNavItems : clientNavItems;

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="min-h-screen bg-[#f8faf8] relative overflow-hidden" data-testid="portal-layout">
      <div className="absolute inset-0 pointer-events-none" data-testid="portal-background">
        <div className="absolute -top-24 right-10 w-80 h-80 bg-[#1F3D2B]/5 rounded-full blur-3xl animate-blob" />
        <div className="absolute top-1/3 -left-20 w-72 h-72 bg-[#1F3D2B]/10 rounded-full blur-3xl animate-blob" style={{ animationDelay: '2s' }} />
        <div className="absolute bottom-10 right-1/3 w-64 h-64 bg-green-600/5 rounded-full blur-3xl animate-float" />
        <div className="absolute inset-0 forest-pattern opacity-20" />
      </div>

      <div className="relative z-10">
      {/* Mobile header */}
      <div className="lg:hidden bg-white/90 backdrop-blur-md border-b border-gray-200 px-4 py-3 flex items-center justify-between" data-testid="portal-mobile-header">
        <div className="flex items-center gap-3">
          <img src={companyInfo.logo.dark} alt="" className="h-8 w-auto" />
          <span className="font-semibold text-[#1F3D2B]">{companyInfo.name}</span>
        </div>
        <div className="flex items-center gap-1">
          <NotificationBell {...notifications} idSuffix="-mobile" />
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-gray-100"
            data-testid="portal-mobile-toggle"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="portal-sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src={companyInfo.logo.dark} alt="" className="h-10 w-auto" />
                <div>
                  <h2 className="font-bold text-[#1F3D2B]">{companyInfo.name}</h2>
                  <p className="text-xs text-[#2E2E2E]/60">{t('portalUi.layout.portalLabel', 'Klientu portāls')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* User info */}
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#1F3D2B]/10 flex items-center justify-center">
                {isAdmin ? (
                  <Shield className="w-5 h-5 text-[#1F3D2B]" />
                ) : isEmployee ? (
                  <Briefcase className="w-5 h-5 text-[#1F3D2B]" />
                ) : (
                  <span className="text-[#1F3D2B] font-semibold">
                    {user?.client_name?.charAt(0) || 'K'}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-[#2E2E2E] truncate" data-testid="portal-user-name">{user?.client_name}</p>
                <p className="text-xs text-[#2E2E2E]/60" data-testid="portal-user-role">
                  {isAdmin
                    ? t('portalUi.layout.roleAdmin', 'Administrators')
                    : isEmployee
                      ? t('portalUi.layout.roleEmployee', 'Darbinieks')
                      : t('portalUi.layout.roleClient', 'Klients')}
                </p>
              </div>
              <div className="hidden lg:block">
                <NotificationBell {...notifications} side="right" align="start" />
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto" data-testid="portal-nav">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                    isActive
                      ? 'bg-[#1F3D2B] text-white shadow-lg shadow-[#1F3D2B]/20'
                      : 'text-[#2E2E2E]/70 hover:bg-[#1F3D2B]/5 hover:text-[#1F3D2B]'
                  }`}
                  data-testid={`portal-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                  {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                </Link>
              );
            })}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-gray-100">
            <Button
              variant="outline"
              className="w-full justify-start gap-3 text-[#2E2E2E]/70 hover:text-red-600 hover:border-red-200 hover:bg-red-50"
              onClick={handleLogout}
              data-testid="portal-logout-button"
            >
              <LogOut className="w-5 h-5" />
              {t('portalUi.layout.logout', 'Izrakstīties')}
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="portal-overlay"
        />
      )}

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen">
        <div className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
      </div>
    </div>
  );
};

export default PortalLayout;
